import { zodResolver } from "@hookform/resolvers/zod";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { Button, DropdownSelect, ErrorMessage, Loading, Panel, Recaptcha, Typography } from "@/components/atoms";
import { AccountType } from "@/constants/accountTypes";
import { getApiHostUrl } from "@/helpers/apiHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { useRegisterClientMutation } from "@/redux/apis/auth/authApi";
import { useGetProfileOptionsQuery, useUpdateProfileMutation } from "@/redux/apis/profile/profileApi";
import { addToast } from "@/utils";
import { ContactNumberField, HowDidYouHearAboutUsField, CompanyField, FirstNameField, LastNameField } from "../SignUp/forms/fields";

// Invited clients are created in Kinde with no name, so we must collect first/last
// name here (self-signup clients get theirs pre-filled from the Kinde profile).
const clientRegistrationSchema = z.object({
  title: z.string({ required_error: "Title is required." }).min(1, "Title is required."),
  firstName: z.string().max(45).min(1, "First name is required."),
  lastName: z.string().max(45).min(1, "Last name is required."),
  company: z.string().optional(),
  contactNumber: z.preprocess((val) => val ?? "", z.string().nonempty("Field is required and can't be empty.")),
  howDidYouHearAboutUs: z
    .string({ required_error: "Field is required and can't be empty." })
    .min(1, "Field is required and can't be empty."),
});

type ClientRegistrationFormData = z.infer<typeof clientRegistrationSchema>;

interface ClientRegistrationStepProps {
  onRegistrationComplete: () => void;
  providerCode: string;
  userType: AccountType;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
}

export const ClientRegistrationStep = ({
  onRegistrationComplete,
  providerCode,
  userType,
  userEmail,
  userFirstName,
  userLastName,
}: ClientRegistrationStepProps) => {
  const operator = useOperator();
  const [registerClient] = useRegisterClientMutation();
  const [updateProfile] = useUpdateProfileMutation();
  const { data: profileOptions } = useGetProfileOptionsQuery();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const methods = useForm<ClientRegistrationFormData>({
    resolver: zodResolver(clientRegistrationSchema),
    defaultValues: {
      firstName: userFirstName,
      lastName: userLastName,
    },
  });

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isDirty },
  } = methods;

  // The Kinde profile (source of the name for self-signup clients) can resolve after
  // this step mounts, and defaultValues only snapshot at mount. Backfill an empty,
  // untouched name field when the props arrive — never overwrite what the user typed.
  useEffect(() => {
    if (userFirstName && !getValues("firstName")) setValue("firstName", userFirstName);
    if (userLastName && !getValues("lastName")) setValue("lastName", userLastName);
  }, [userFirstName, userLastName, getValues, setValue]);

  const onRecaptchaChange = (token: string | null) => setRecaptchaToken(token);

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      // register/client sets the account name, mirroring the mobile completion step.
      await registerClient({
        apiUrl: getApiHostUrl(providerCode),
        first_name: data.firstName,
        last_name: data.lastName,
        company_name: data.company,
        email: userEmail,
        mobile: data.contactNumber,
        hear_about_us: data.howDidYouHearAboutUs,
        provider_code: providerCode,
        type: userType,
        registered_device: "web-app",
      }).unwrap();

      // Persist the honorific title (register/client doesn't take it) and mark
      // registration complete — mirrors the mobile completion step.
      await updateProfile({
        title: data.title,
        account_metadata: {
          is_register_complete: true,
          provider_code: providerCode,
          type: userType,
        },
      }).unwrap();

      addToast("success", "Welcome! Your account is ready");
      onRegistrationComplete();
    } catch (error) {
      console.error("Error completing client registration:", error);
      getErrorMessages(error as FetchBaseQueryError | SerializedError).forEach((m) => addToast("danger", m));
      setIsSubmitting(false);
    }
  });

  return (
    <Panel className="relative w-full max-w-[680px] p-8">
      {isSubmitting && <Loading className="rounded-lg" />}
      <FormProvider {...methods}>
        <form onSubmit={onSubmit}>
          <div className="mb-7 flex flex-row items-center">
            <div className="flex flex-1 flex-col gap-y-1.5">
              <Typography variant="h1">{`Complete your registration`}</Typography>
              <Typography variant="paragraph">{`Finish setting up your account with ${operator.name}`}</Typography>
            </div>
            {operator.images?.emblem && (
              <img src={operator.images.emblem} alt={`${operator.name} emblem`} className="h-16 w-16 object-contain" />
            )}
          </div>

          <div className="grid animate-enter grid-cols-2 gap-x-2.5 gap-y-3 lg:gap-x-5 lg:gap-y-6">
            <div className="col-span-2 flex flex-col gap-y-1 lg:col-span-1">
              <Typography variant="paragraph">Title</Typography>
              <Controller
                control={control}
                name="title"
                render={({ field }) => (
                  <DropdownSelect
                    className="w-full"
                    placeholder="Select Your Title"
                    options={profileOptions?.titles ?? []}
                    value={field.value}
                    onChange={(value) => field.onChange(value)}
                    hasError={!!errors.title}
                  />
                )}
              />
              <ErrorMessage errors={errors} name="title" />
            </div>
            <FirstNameField className="col-span-2 lg:col-span-1" />
            <LastNameField className="col-span-2 lg:col-span-1" />
            <CompanyField className="col-span-2 lg:col-span-1" />
            <ContactNumberField className="col-span-2 lg:col-span-1" />
            <HowDidYouHearAboutUsField className="col-span-2 lg:col-span-1" />
            <Recaptcha onChange={onRecaptchaChange} className="col-span-2" />
          </div>

          <Button variant="primary" type="submit" size="lg" className="mt-8 w-full" disabled={!isDirty || isSubmitting || !recaptchaToken}>
            Complete Registration
          </Button>
        </form>
      </FormProvider>
    </Panel>
  );
};
